
import React, { useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { PageHeaderCard } from "@/components/layout/PageHeaderCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Edit, Trash2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export default function AdminPlans() {
  const { data: plans, isLoading, refetch } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('price', { ascending: true });

      if (error) {
        console.error('Error fetching plans:', error);
        return [];
      }

      return data || [];
    }
  });

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const formatFeatures = (features: any) => {
    if (!features || typeof features !== 'object') return [];
    return Object.entries(features).map(([key, value]) => `${key}: ${value}`);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <PageHeaderCard
          eyebrow="Administração"
          title="Planos"
          subtitle="Gerencie os planos de assinatura disponíveis na plataforma."
          backTo="/admin/control-center"
          rightTop={
            <Button className="gap-2">
              <Plus size={16} />
              Novo Plano
            </Button>
          }
        />

        <Card className="rounded-2xl border-border/60 shadow-sm">
          <CardHeader>
            <CardTitle>Planos Disponíveis</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="text-gray-500">Carregando planos...</div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Preço</TableHead>
                    <TableHead>Limites</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plans?.map((plan) => (
                    <TableRow key={plan.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{plan.name}</div>
                          {plan.description && (
                            <div className="text-sm text-gray-500">{plan.description}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{formatPrice(plan.price)}</TableCell>
                      <TableCell>
                        <div className="text-sm space-y-1">
                          {plan.max_users && <div>Usuários: {plan.max_users}</div>}
                          {plan.max_services && <div>Serviços: {plan.max_services}</div>}
                          {plan.max_appointments && <div>Agendamentos: {plan.max_appointments}</div>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={plan.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {plan.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {new Date(plan.created_at).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm">
                            <Edit size={16} />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
